import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };

const GATES = [
  ['abr', 'ABR identity', 'The insured’s ABN verified against the Australian Business Register'],
  ['version_pins', 'Version pins', 'Wording, question set, template and rate versions fixed for the term'],
  ['bind_blocks', 'Disclosed circumstances', 'Last year’s resolutions re-attach — a DECLINED outcome cannot bind'],
  ['cyber_panel', 'Cyber panel (PF-12)', 'No cyber policy binds without an incident-response panel on retainer'],
];

export const RenewalBindGates = ({ renewalId, onChanged }) => {
  const api = useApi();
  const { user } = useAuth();
  const isStaff = ['platform_owner', 'mga_admin', 'underwriter'].includes(user?.role);
  const [g, setG] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/renewals/${renewalId}/bind-gates`)
      .then(r => setG(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not read the bind gates'));
  }, [api, renewalId]);
  useEffect(() => { load(); }, [load]);

  const override = async () => {
    setBusy(true);
    try {
      await api.post(`/renewals/${renewalId}/abr-override`, { reason });
      toast.success('Override recorded — audit-logged against your name');
      setReason('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Override failed'); }
    setBusy(false);
  };

  const retry = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/renewals/${renewalId}/action`, { action: 'accept' });
      toast.success(`Renewal bound — ${data.new_policy_number || data.new_policy_id}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bind still blocked'); load(); }
    setBusy(false);
  };

  if (!g) return <p className="text-xs" style={{ color: C.slate }}>Checking the bind gates…</p>;
  const abr = g.checks?.abr || {};

  return (
    <div className="space-y-2" data-testid={`bind-gates-${renewalId}`}>
      <div className="flex items-center gap-2">
        {g.blocked ? <ShieldAlert className="w-4 h-4" style={{ color: C.red }} />
          : <ShieldCheck className="w-4 h-4" style={{ color: C.green }} />}
        <p className="text-xs font-semibold" style={{ color: g.blocked ? C.red : C.green }} data-testid={`bind-gates-verdict-${renewalId}`}>
          {g.blocked ? 'BIND BLOCKED — a renewal passes the same gates as new business'
            : g.simulation ? 'Simulated book — gates reported, not enforced'
              : 'All gates clear — this renewal can bind'}
        </p>
      </div>
      <table className="w-full text-xs">
        <tbody>
          {GATES.map(([key, label, hint]) => {
            const c = g.checks?.[key] || {};
            const bad = c.blocked;
            return (
              <tr key={key} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`gate-${key}-${renewalId}`}>
                <td className="py-1.5 pr-3 w-52">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                    style={{ background: (bad ? C.red : C.green) + '15', color: bad ? C.red : C.green }}>
                    {bad ? 'blocked' : 'clear'}
                  </span>
                  <span className="ml-2 font-medium" style={{ color: C.navy }}>{label}</span>
                </td>
                <td className="py-1.5 text-[11px]" style={{ color: bad ? '#B03230' : C.slate }}>
                  {(c.reasons || []).join(' ') || c.note || hint}
                  {key === 'abr' && c.status ? <span className="ml-1 font-mono text-[10px]">({c.status})</span> : null}
                  {key === 'version_pins' && c.pins ? (
                    <span className="ml-1 font-mono text-[10px]">wording {c.pins.wording_version || '—'} · questions {c.pins.question_set_version || '—'} · rates {c.pins.rate_version || '—'} · templates {c.pins.template_version || '—'}</span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {isStaff && abr.blocked && abr.overridable && !abr.override && (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Why this ABN is safe to bind without the live ABR check" className="rounded-sm text-xs h-8 w-96"
            data-testid={`abr-override-reason-${renewalId}`} />
          <Button size="sm" disabled={busy || reason.trim().length < 10} className="rounded-sm text-xs" style={{ background: C.amber }}
            onClick={override} data-testid={`abr-override-btn-${renewalId}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Record override'}
          </Button>
        </div>
      )}
      {isStaff && !g.blocked && (
        <Button size="sm" disabled={busy} className="rounded-sm text-xs" style={{ background: C.navy }}
          onClick={retry} data-testid={`bind-retry-${renewalId}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Bind the renewal'}
        </Button>
      )}
    </div>
  );
};
